import { useState } from "react";
import {
  FormConfig,
  KnowledgeBaseForm,
  LearningObjective,
  StructureKb,
  Theme,
  Topic,
} from "src/interfaces/KnowledgeBase";
import { stringValidator, formatString } from "src/utils/helpers";

export const useManualDocsUpload = (): KnowledgeBaseForm => {
  const [themes, setThemes] = useState<Theme[]>([
    {
      name: "",
      topics: [
        {
          name: "",
          learning_objectives: [{ name: "" }],
        },
      ],
    },
  ]);
  const [config, setConfig] = useState<FormConfig | null>(null);

  const setConfigFromStructure = (structureKb: string) => {
    try {
      const parsed = JSON.parse(structureKb);
      const hasContent =
        !!parsed.themes?.name?.topics?.name?.learning_objectives?.name?.content;
      setConfig({
        themeLabel: parsed.themes?.name?.label || "Theme",
        topicLabel: parsed.themes?.name?.topics?.name?.label || "Topic",
        learningObjectiveLabel:
          parsed.themes?.name?.topics?.name?.learning_objectives?.name?.label ||
          "Learning Objective",
        ...(hasContent && {
          contentLabel:
            parsed.themes?.name?.topics?.name?.learning_objectives?.name
              ?.content?.label || "Content",
          contentPlaceholder:
            parsed.themes?.name?.topics?.name?.learning_objectives?.name
              ?.content?.value || "Enter description",
        }),
      });
      setThemes([
        {
          name: "",
          topics: [
            {
              name: "",
              learning_objectives: [
                { name: "", ...(hasContent && { content: "" }) },
              ],
            },
          ],
        },
      ]);
    } catch (error) {
      console.error("Error parsing structure_kb:", error);
      setConfig(null);
    }
  };

  const addTheme = () => {
    setThemes([
      ...themes,
      {
        name: "",
        topics: [
          {
            name: "",
            learning_objectives: [
              { name: "", ...(config?.contentLabel ? { content: "" } : {}) },
            ],
          },
        ],
      },
    ]);
  };

  const removeTheme = (themeIndex: number) => {
    if (themes.length <= 1) return;
    setThemes(themes.filter((_, idx) => idx !== themeIndex));
  };

  const updateThemeName = (themeIndex: number, value: string) => {
    const newThemes = [...themes];
    newThemes[themeIndex].name = value;
    setThemes(newThemes);
  };

  const addTopic = (themeIndex: number) => {
    const newThemes = [...themes];
    newThemes[themeIndex].topics.push({
      name: "",
      learning_objectives: [
        { name: "", ...(config?.contentLabel ? { content: "" } : {}) },
      ],
    });
    setThemes(newThemes);
  };

  const removeTopic = (themeIndex: number, topicIndex: number) => {
    const newThemes = [...themes];
    if (newThemes[themeIndex].topics.length <= 1) return;
    newThemes[themeIndex].topics.splice(topicIndex, 1);
    setThemes(newThemes);
  };

  const updateTopicName = (
    themeIndex: number,
    topicIndex: number,
    value: string
  ) => {
    const newThemes = [...themes];
    newThemes[themeIndex].topics[topicIndex].name = value;
    setThemes(newThemes);
  };

  const addLearningObjective = (themeIndex: number, topicIndex: number) => {
    const newThemes = [...themes];
    newThemes[themeIndex].topics[topicIndex].learning_objectives.push({
      name: "",
      ...(config?.contentLabel ? { content: "" } : {}),
    });
    setThemes(newThemes);
  };

  const removeLearningObjective = (
    themeIndex: number,
    topicIndex: number,
    loIndex: number
  ) => {
    const newThemes = [...themes];
    if (
      newThemes[themeIndex].topics[topicIndex].learning_objectives.length <= 1
    )
      return;
    newThemes[themeIndex].topics[topicIndex].learning_objectives.splice(
      loIndex,
      1
    );
    setThemes(newThemes);
  };

  const updateLearningObjectiveName = (
    themeIndex: number,
    topicIndex: number,
    loIndex: number,
    value: string
  ) => {
    const newThemes = [...themes];
    newThemes[themeIndex].topics[topicIndex].learning_objectives[loIndex].name =
      value;
    setThemes(newThemes);
  };

  const updateContent = (
    themeIndex: number,
    topicIndex: number,
    loIndex: number,
    value: string
  ) => {
    const newThemes = [...themes];
    newThemes[themeIndex].topics[topicIndex].learning_objectives[
      loIndex
    ].content = value;
    setThemes(newThemes);
  };

  const validate = () => {
    return (
      themes.length > 0 &&
      themes.every(
        (theme) =>
          stringValidator(theme.name) &&
          theme.topics.length > 0 &&
          theme.topics.every(
            (topic) =>
              stringValidator(topic.name) &&
              topic.learning_objectives.length > 0 &&
              topic.learning_objectives.every(
                (lo) =>
                  stringValidator(lo.name) &&
                  (!config?.contentLabel || stringValidator(lo.content || ""))
              )
          )
      )
    );
  };

  const reset = () => {
    setThemes([
      {
        name: "",
        topics: [
          {
            name: "",
            learning_objectives: [
              { name: "", ...(config?.contentLabel ? { content: "" } : {}) },
            ],
          },
        ],
      },
    ]);
  };

  const getFormData = () => {
    if (!config) return null;
    const result: any = {
      themes: {},
    };

    themes.forEach((theme) => {
      const themeKey = formatString(theme.name);

      result.themes[themeKey] = {
        label: config.themeLabel,
        type: "text",
        required: true,
        topics: {},
      };

      theme.topics.forEach((topic) => {
        const topicKey = formatString(topic.name);

        result.themes[themeKey].topics[topicKey] = {
          label: config.topicLabel,
          type: "text",
          required: true,
          learning_objectives: {},
        };

        topic.learning_objectives.forEach((lo) => {
          const loKey = formatString(lo.name);

          result.themes[themeKey].topics[topicKey].learning_objectives[loKey] =
            {
              label: config.learningObjectiveLabel,
              type: "text",
              required: true,
              ...(config.contentLabel && lo.content !== undefined
                ? {
                    content: {
                      value: lo.content,
                      label: config.contentLabel,
                      type: "textarea",
                      required: true,
                    },
                  }
                : {}),
            };
        });
      });
    });

    return result;
  };

  const prefillForm = (data: StructureKb | string) => {
    let useableData: StructureKb;
    try {
      useableData = typeof data === "string" ? JSON.parse(data) : data;
    } catch (error) {
      console.error("Error parsing input data:", error);
      setThemes([
        {
          name: "",
          topics: [
            {
              name: "",
              learning_objectives: [{ name: "" }],
            },
          ],
        },
      ]);
      setConfig({
        themeLabel: "Theme",
        topicLabel: "Topic",
        learningObjectiveLabel: "Learning Objective",
      });
      return;
    }

    try {
      const themeObj = useableData?.themes;
      if (!themeObj || typeof themeObj !== "object") {
        throw new Error("Invalid themes structure");
      }

      // Type guard to check if it's a template structure
      const isTemplate = (
        obj: any
      ): obj is {
        topics?: {
          label?: string;
          type?: string;
          required?: boolean;
          learning_objectives?: any;
        };
      } => {
        return (
          obj &&
          typeof obj === "object" &&
          obj.topics &&
          typeof obj.topics === "object" &&
          ("label" in obj.topics ||
            "type" in obj.topics ||
            "required" in obj.topics)
        );
      };

      // Check if any theme value is a template structure
      const hasTemplateStructure = Object.values(themeObj).some(isTemplate);

      let tempThemeLabel: string = "Theme";
      let tempTopicLabel: string = "Topic";
      let tempLoLabel: string = "Learning Objective";
      let tempContentLabel: string | undefined;
      let tempContentPlaceholder: string | undefined;
      let hasContent = false;

      if (hasTemplateStructure) {
        // Handle template structure
        const templateTheme = Object.values(themeObj).find(isTemplate);
        if (templateTheme?.topics) {
          tempTopicLabel =
            typeof templateTheme.topics.label === "string"
              ? templateTheme.topics.label
              : "Topic";

          if (templateTheme.topics.learning_objectives) {
            tempLoLabel =
              typeof templateTheme.topics.learning_objectives.label === "string"
                ? templateTheme.topics.learning_objectives.label
                : "Learning Objective";

            if (templateTheme.topics.learning_objectives.content) {
              hasContent = true;
              tempContentLabel =
                typeof templateTheme.topics.learning_objectives.content
                  .label === "string"
                  ? templateTheme.topics.learning_objectives.content.label
                  : "Content";
              tempContentPlaceholder =
                typeof templateTheme.topics.learning_objectives.content
                  .value === "string"
                  ? templateTheme.topics.learning_objectives.content.value
                  : "Enter description";
            }
          }
        }
      } else {
        // Handle data structure - find labels and check for content
        for (const [themeKey, themeValue] of Object.entries(themeObj)) {
          if (typeof themeValue === "object" && themeValue !== null) {
            // Extract theme label
            if ("label" in themeValue && typeof themeValue.label === "string") {
              tempThemeLabel = themeValue.label;
            }

            // Check topics
            if (
              "topics" in themeValue &&
              themeValue.topics &&
              typeof themeValue.topics === "object"
            ) {
              for (const [topicKey, topicValue] of Object.entries(
                themeValue.topics
              )) {
                if (typeof topicValue === "object" && topicValue !== null) {
                  // Extract topic label
                  if (
                    "label" in topicValue &&
                    typeof topicValue.label === "string"
                  ) {
                    tempTopicLabel = topicValue.label;
                  }

                  // Check learning objectives
                  if (
                    "learning_objectives" in topicValue &&
                    topicValue.learning_objectives &&
                    typeof topicValue.learning_objectives === "object"
                  ) {
                    for (const [loKey, loValue] of Object.entries(
                      topicValue.learning_objectives
                    )) {
                      if (typeof loValue === "object" && loValue !== null) {
                        // Extract learning objective label
                        if (
                          "label" in loValue &&
                          typeof loValue.label === "string"
                        ) {
                          tempLoLabel = loValue.label;
                        }

                        // Check for content
                        if (
                          "content" in loValue &&
                          loValue.content &&
                          typeof loValue.content === "object"
                        ) {
                          hasContent = true;
                          if (
                            "label" in loValue.content &&
                            typeof loValue.content.label === "string"
                          ) {
                            tempContentLabel = loValue.content.label;
                          } else {
                            tempContentLabel = "Content";
                          }
                          if (
                            "value" in loValue.content &&
                            typeof loValue.content.value === "string"
                          ) {
                            tempContentPlaceholder = loValue.content.value;
                          } else {
                            tempContentPlaceholder = "Enter description";
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }

      setConfig({
        themeLabel: tempThemeLabel,
        topicLabel: tempTopicLabel,
        learningObjectiveLabel: tempLoLabel,
        ...(hasContent && {
          contentLabel: tempContentLabel || "Content",
          contentPlaceholder: tempContentPlaceholder || "Enter description",
        }),
      });

      let newThemes: Theme[] = [];

      if (hasTemplateStructure) {
        // Create empty structure for template
        newThemes = [
          {
            name: "",
            topics: [
              {
                name: "",
                learning_objectives: [
                  { name: "", ...(hasContent ? { content: "" } : {}) },
                ],
              },
            ],
          },
        ];
      } else {
        // Process actual data structure
        newThemes = Object.entries(themeObj)
          .filter(([key]) => !["label", "type", "required"].includes(key))
          .map(([themeName, themeData]) => {
            if (typeof themeData !== "object" || themeData === null) {
              return {
                name: themeName,
                topics: [
                  {
                    name: "",
                    learning_objectives: [
                      { name: "", ...(hasContent ? { content: "" } : {}) },
                    ],
                  },
                ],
              };
            }

            const topics: Topic[] = [];

            if (
              "topics" in themeData &&
              themeData.topics &&
              typeof themeData.topics === "object"
            ) {
              for (const [topicName, topicData] of Object.entries(
                themeData.topics
              )) {
                if (
                  !["label", "type", "required"].includes(topicName) &&
                  typeof topicData === "object" &&
                  topicData !== null
                ) {
                  const learningObjectives: LearningObjective[] = [];

                  if (
                    "learning_objectives" in topicData &&
                    topicData.learning_objectives &&
                    typeof topicData.learning_objectives === "object"
                  ) {
                    for (const [loName, loData] of Object.entries(
                      topicData.learning_objectives
                    )) {
                      if (
                        !["label", "type", "required"].includes(loName) &&
                        typeof loData === "object" &&
                        loData !== null
                      ) {
                        const learningObjective: LearningObjective = {
                          name: loName,
                        };

                        if (
                          hasContent &&
                          "content" in loData &&
                          loData.content &&
                          typeof loData.content === "object" &&
                          "value" in loData.content
                        ) {
                          learningObjective.content = String(
                            loData.content.value || ""
                          );
                        } else if (hasContent) {
                          learningObjective.content = "";
                        }

                        learningObjectives.push(learningObjective);
                      }
                    }
                  }

                  topics.push({
                    name: topicName,
                    learning_objectives:
                      learningObjectives.length > 0
                        ? learningObjectives
                        : [
                            {
                              name: "",
                              ...(hasContent ? { content: "" } : {}),
                            },
                          ],
                  });
                }
              }
            }

            return {
              name: themeName,
              topics:
                topics.length > 0
                  ? topics
                  : [
                      {
                        name: "",
                        learning_objectives: [
                          { name: "", ...(hasContent ? { content: "" } : {}) },
                        ],
                      },
                    ],
            };
          });
      }

      setThemes(
        newThemes.length > 0
          ? newThemes
          : [
              {
                name: "",
                topics: [
                  {
                    name: "",
                    learning_objectives: [
                      { name: "", ...(hasContent ? { content: "" } : {}) },
                    ],
                  },
                ],
              },
            ]
      );
    } catch (error) {
      console.error("Error prefilling form:", error);
      setThemes([
        {
          name: "",
          topics: [
            {
              name: "",
              learning_objectives: [{ name: "" }],
            },
          ],
        },
      ]);
      setConfig({
        themeLabel: "Theme",
        topicLabel: "Topic",
        learningObjectiveLabel: "Learning Objective",
      });
    }
  };

  return {
    themes,
    config,
    addTheme,
    removeTheme,
    updateThemeName,
    addTopic,
    removeTopic,
    updateTopicName,
    addLearningObjective,
    removeLearningObjective,
    updateLearningObjectiveName,
    updateContent,
    validate,
    reset,
    getFormData,
    setConfigFromStructure,
    prefillForm,
  };
};
